import { Application, Graphics } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import * as api from './api';
import { apiNodesToNodeData, fetchNodes } from './api';
import { buildLayout, makeNodeLayout } from './layout';
import { createNodeSprite, drawLines, applyThemeToSprites } from './renderer';
import { setupInteractions } from './interactions';
import { setupPanel } from './panel';
import { setupEventPanel } from './eventPanel';
import { currentTheme, toggleTheme, applyCssTheme } from './theme';
import { iconSvg } from './icons';
import { openTextPrompt } from './modal';
import { renderPagesView } from './pages';
import { renderApiDocs } from './apiDocs';
import type { NodeSprite } from './renderer';

// ─── Map view (route: /page/:id) ────────────────────────────────────────────

async function initMap(pageId: string): Promise<void> {

  // ── 1. Apply initial theme CSS ──────────────────────────────────────────
  applyCssTheme();

  // ── 2. Application PixiJS ───────────────────────────────────────────────
  const app = new Application();

  await app.init({
    backgroundColor: currentTheme().background,
    resizeTo:        window,
    antialias:       false,
    resolution:      window.devicePixelRatio || 1,
    autoDensity:     true,
    autoStart:       false,
  });

  // We render on demand — keep the ticker permanently stopped.
  app.ticker.stop();

  document.body.appendChild(app.canvas);

  // ── Render-on-demand helper (coalesces multiple requests into one frame) ─
  let renderScheduled = false;
  const requestRender = (): void => {
    if (renderScheduled) return;
    renderScheduled = true;
    requestAnimationFrame(() => {
      renderScheduled = false;
      app.renderer.render(app.stage);
    });
  };

  // ── 3. Viewport ─────────────────────────────────────────────────────────
  const viewport = new Viewport({
    screenWidth:  window.innerWidth,
    screenHeight: window.innerHeight,
    worldWidth:   window.innerWidth,
    worldHeight:  window.innerHeight,
    events:       app.renderer.events,
  });

  app.stage.addChild(viewport);
  app.stage.sortableChildren = true;
  viewport.sortableChildren  = true;

  viewport
    .drag()
    .pinch()
    .wheel();

  // Any viewport motion (drag / wheel zoom / pinch) needs a redraw.
  viewport.on('moved',  requestRender);
  viewport.on('zoomed', requestRender);

  // ── Persist pan/zoom per page, restore on refresh ────────────────────────
  // 'moved-end' / 'zoomed-end' fire only after a user drag / wheel / pinch
  // settles — not on programmatic pans (event zooms), so we save the user's
  // last position, not wherever an event last panned to.
  const VIEWPORT_KEY = `dictator-viewport-${pageId}`;
  const saveViewport = (): void => {
    try {
      window.localStorage.setItem(VIEWPORT_KEY, JSON.stringify({
        x: viewport.center.x, y: viewport.center.y, scale: viewport.scale.x,
      }));
    } catch { /* storage unavailable */ }
  };
  const restoreViewport = (): void => {
    try {
      const raw = window.localStorage.getItem(VIEWPORT_KEY);
      if (!raw) return;
      const v = JSON.parse(raw) as { x?: unknown; y?: unknown; scale?: unknown };
      if (typeof v.x === 'number' && typeof v.y === 'number' && typeof v.scale === 'number') {
        viewport.scale.set(v.scale);
        viewport.moveCenter(v.x, v.y);
      }
    } catch { /* ignore malformed */ }
  };
  viewport.on('moved-end',  saveViewport);
  viewport.on('zoomed-end', saveViewport);

  // ── 4. Fetch nodes from backend, build layout ───────────────────────────
  const rows    = await fetchNodes(pageId);
  const nodes   = apiNodesToNodeData(rows);
  const layouts = buildLayout(nodes, window.innerWidth, window.innerHeight);

  // Apply stored positions where present; collect missing positions to persist.
  const missing: { id: string; x: number; y: number }[] = [];
  for (const row of rows) {
    const layout = layouts.get(row.id);
    if (!layout) continue;
    if (row.x !== null && row.y !== null) {
      layout.x = row.x;
      layout.y = row.y;
    } else {
      missing.push({ id: layout.id, x: layout.x, y: layout.y });
    }
  }
  if (missing.length > 0) api.bulkPositions(missing);

  // ── 5. Lines layer ──────────────────────────────────────────────────────
  const linesGraphics = new Graphics();
  linesGraphics.zIndex = 0;
  viewport.addChild(linesGraphics);

  // ── 7. Sprites des nœuds ────────────────────────────────────────────────
  const sprites = new Map<string, NodeSprite>();

  for (const [id, layout] of layouts) {
    const sprite = createNodeSprite(layout, currentTheme());
    sprite.container.zIndex = Math.max(1, 4 - layout.level);
    sprites.set(id, sprite);
    viewport.addChild(sprite.container);
  }

  // ── 8. Dessin initial + interactions + panel ────────────────────────────
  drawLines(linesGraphics, layouts, currentTheme());

  // Forward declaration: the panel is created below but interactions may need
  // to refresh it on add/delete. Use a mutable holder.
  const panelRef: { handle: ReturnType<typeof setupPanel> | null } = { handle: null };
  const refreshPanel = (): void => panelRef.handle?.refresh();

  // The event panel reacts to canvas selection; created below, so route the
  // selection callback through a mutable holder (same pattern as panelRef).
  const eventPanelRef: { handle: ReturnType<typeof setupEventPanel> | null } = { handle: null };

  const interactions = setupInteractions(
    viewport, sprites, layouts, linesGraphics, currentTheme, requestRender, pageId, refreshPanel,
    (layout) => eventPanelRef.handle?.setNode(layout?.id ?? null),
  );

  panelRef.handle = setupPanel(
    layouts,
    interactions.focusOn,
    interactions.pulseNode,
    interactions.selectNode,
  );

  eventPanelRef.handle = setupEventPanel(
    pageId,
    layouts,
    (nodeId, status) => interactions.playEventAnimation(nodeId, status),
  );

  // ── Live event polling (this page only) ──────────────────────────────────
  // Poll the page-wide event stream and react on the canvas: zoom to the target
  // node, pulse it, run the status/label effects, and prepend it to the event
  // panel. The first poll only seeds the cursor (no history replay).
  const POLL_INTERVAL_MS = 4000;
  const seenEvents = new Set<string>();
  let pollCursor: string | undefined;

  const reactToEvent = (e: api.ApiEvent): void => {
    // Prepend the new event to the list (no full reload) and flag it as the
    // currently-playing card — keeps the panel's scope.
    eventPanelRef.handle?.addEvent(e);
    eventPanelRef.handle?.markPlaying(e.id);

    // Apply the persisted status/label — which fire the done/blocked/bounce
    // effects — deferred until the zoom lands.
    const applyEffects = (): void => {
      if (e.status) interactions.applyStatusVisual(e.node_id, e.status);
      if (e.label)  interactions.applyLabelVisual(e.node_id, e.label);
    };

    // Zoom to the node first (pulse on arrival), then run the effects. If the
    // node isn't on this page there's no zoom — run them immediately.
    if (!interactions.pulseEvent(e.node_id, applyEffects)) applyEffects();
  };

  // ── Playback queue ────────────────────────────────────────────────────────
  // Events are queued and played one at a time, every 3s, so two events never
  // animate simultaneously (a poll can return a whole burst at once).
  const EVENT_PLAY_INTERVAL_MS = 3000;
  const MAX_QUEUE = 40; // bound memory during long/fast bursts
  const eventQueue: api.ApiEvent[] = [];
  let playing = false;

  const playNextQueued = (): void => {
    const e = eventQueue.shift();
    if (!e) { playing = false; eventPanelRef.handle?.markPlaying(null); return; }
    reactToEvent(e);
    window.setTimeout(playNextQueued, EVENT_PLAY_INTERVAL_MS);
  };

  const enqueueEvent = (e: api.ApiEvent): void => {
    eventQueue.push(e);
    if (eventQueue.length > MAX_QUEUE) {
      const dropped = eventQueue.splice(0, eventQueue.length - MAX_QUEUE);
      console.warn(`[poll] event queue over ${MAX_QUEUE}; dropped ${dropped.length} oldest`);
    }
    if (!playing) { playing = true; playNextQueued(); } // play the first immediately
  };

  const pollOnce = async (): Promise<void> => {
    try {
      const { events, cursor } = await api.fetchPageEvents(pageId, pollCursor);
      pollCursor = cursor;
      for (const e of events) {
        if (seenEvents.has(e.id)) continue;
        seenEvents.add(e.id);
        enqueueEvent(e);
      }
    } catch (err) {
      console.warn('[poll] fetchPageEvents failed', err);
    }
  };

  const scheduleNextPoll = (): void => {
    window.setTimeout(async () => {
      if (!document.hidden) await pollOnce(); // don't hammer the API in a hidden tab
      scheduleNextPoll();
    }, POLL_INTERVAL_MS);
  };
  void pollOnce().finally(scheduleNextPoll); // seed cursor, then loop

  // ── 9. Résolution du texte adaptée au zoom ───────────────────────────────
  // Re-rasterizing 75 PIXI.Text textures on every wheel tick is the bottleneck.
  // Solution: snap to integer tiers (1/2/3) and debounce until zoom settles.
  let currentTextRes = 0;
  let zoomDebounce: number | null = null;

  const applyTextResolution = (): void => {
    const raw  = viewport.scale.x * (window.devicePixelRatio || 1);
    const tier = Math.max(1, Math.min(3, Math.ceil(raw)));
    if (tier === currentTextRes) return;
    currentTextRes = tier;
    for (const sprite of sprites.values()) {
      sprite.text.resolution = tier;
    }
    requestRender();
  };

  viewport.on('zoomed', () => {
    if (zoomDebounce !== null) clearTimeout(zoomDebounce);
    zoomDebounce = window.setTimeout(() => {
      zoomDebounce = null;
      applyTextResolution();
    }, 120);
  });
  applyTextResolution();

  // ── Back to pages list ──────────────────────────────────────────────────
  const backBtn = document.getElementById('back-btn') as HTMLButtonElement;
  backBtn.innerHTML = `${iconSvg('panelRight', 16)}<span>Pages</span>`;
  backBtn.addEventListener('click', () => window.location.assign('/'));

  // ── Dictator logo → pages list ──────────────────────────────────────────
  const title = document.getElementById('title') as HTMLElement;
  title.addEventListener('click', () => window.location.assign('/'));

  // ── Panel toggle (top bar) + close button (inside panel header) ─────────
  const panelToggle = document.getElementById('panel-toggle') as HTMLButtonElement;
  const panelClose  = document.getElementById('panel-close')  as HTMLButtonElement;
  panelToggle.innerHTML = `${iconSvg('panelRight', 16)}<span>Nodes</span>`;
  panelClose.innerHTML  = iconSvg('x', 18);

  panelToggle.addEventListener('click', () => {
    document.body.classList.remove('events-open'); // only one right panel at a time
    document.body.classList.toggle('panel-open');
  });
  panelClose.addEventListener('click', () => {
    document.body.classList.remove('panel-open');
  });

  // ── Event panel toggle (lightning) + its close button ───────────────────
  const eventsToggle    = document.getElementById('events-toggle')     as HTMLButtonElement;
  const eventPanelClose = document.getElementById('event-panel-close') as HTMLButtonElement;
  const eventPanelTitle = document.getElementById('event-panel-title') as HTMLSpanElement;
  eventsToggle.innerHTML    = `${iconSvg('zap', 16)}<span>Events</span>`;
  eventPanelTitle.innerHTML = `${iconSvg('zap', 16)}<span>Events</span>`;
  eventPanelClose.innerHTML = iconSvg('x', 18);

  eventsToggle.addEventListener('click', () => {
    document.body.classList.remove('panel-open'); // only one right panel at a time
    document.body.classList.toggle('events-open');
    eventPanelRef.handle?.setNode(interactions.getSelectedId());
  });
  eventPanelClose.addEventListener('click', () => {
    document.body.classList.remove('events-open');
  });

  // ── 10. Theme toggle button ─────────────────────────────────────────────
  const themeBtn = document.getElementById('theme-toggle') as HTMLButtonElement;
  const setThemeBtnContent = (mode: 'night' | 'day'): void => {
    // In night mode, button offers a switch to day (sun). In day mode, to night (moon).
    themeBtn.innerHTML = mode === 'night' ? iconSvg('sun', 16) : iconSvg('moon', 16);
    themeBtn.setAttribute('aria-label', mode === 'night' ? 'Switch to day theme' : 'Switch to night theme');
  };
  setThemeBtnContent(currentTheme().mode);

  themeBtn.addEventListener('click', () => {
    const theme = toggleTheme();
    applyCssTheme();

    app.renderer.background.color = theme.background;
    interactions.redrawLines();
    applyThemeToSprites(sprites, theme);
    setThemeBtnContent(theme.mode);

    requestRender();
  });

  // ── 10b. Add (new project) button ───────────────────────────────────────
  const addBtn = document.getElementById('add-btn') as HTMLButtonElement;
  addBtn.innerHTML = `${iconSvg('plus', 16)}<span>Add</span>`;

  addBtn.addEventListener('click', async () => {
    const label = await openTextPrompt('New project');
    if (!label) return;

    let apiNode;
    try {
      apiNode = await api.createNode(pageId, label, null);
    } catch (err) {
      console.warn('[api] createNode failed', err);
      return;
    }

    // Place the new root at the current viewport center so it's visible.
    const layout = makeNodeLayout({
      id:       apiNode.id,
      label:    apiNode.label,
      level:    0,
      status:   apiNode.status,
      parentId: null,
      childIds: [],
      x:        viewport.center.x,
      y:        viewport.center.y,
    });
    layouts.set(layout.id, layout);
    interactions.registerNode(layout);
    drawLines(linesGraphics, layouts, currentTheme());
    api.patchPosition(layout.id, layout.x, layout.y);
    requestRender();
    refreshPanel();
  });

  // ── 11. Redimensionnement ────────────────────────────────────────────────
  window.addEventListener('resize', () => {
    viewport.resize(window.innerWidth, window.innerHeight);
    requestRender();
  });

  // ── 12. Restore the saved pan/zoom (if any), then initial render ─────────
  restoreViewport();
  requestRender();
}

// ─── Router ──────────────────────────────────────────────────────────────────
// "/page/:id" → map view; anything else → pages list. Navigation between the
// two uses full page loads (location.assign), so there is no PixiJS teardown.

function boot(): void {
  const path  = window.location.pathname;
  const match = path.match(/^\/page\/([^/]+)$/);
  if (match) {
    document.body.classList.add('route-map');
    initMap(decodeURIComponent(match[1] as string)).catch(console.error);
  } else if (path === '/api') {
    document.body.classList.add('route-api');
    renderApiDocs();
  } else {
    document.body.classList.add('route-list');
    renderPagesView().catch(console.error);
  }
}

boot();
